import {
  Box,
  Divider,
  HStack,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  DistributionGroupFragment,
  MessageThreadType,
  RemovedUserFragment,
  SearchFilterType,
  UserFragment,
  UserRoleType,
} from "gql/graphql";
import {
  BellOutlineSlashIcon,
  ExternalIcon,
  EyeSlashIcon,
  UserEmailCircleIcon,
} from "modules/icons";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { useUsers } from "modules/providers/users-provider";
import moment from "moment";
import React, { memo, useMemo } from "react";
import colors from "app/colors";
import Link from "../layouts/_shared/link";
import UserMention from "./mentions/user-mention";
import UserAvatar from "./user-avatar";
import useSearchFilter from "modules/hooks/use-search-filter";
import {
  TAG_GROUP_THREADS_PATH,
  COMMON_DATE_CALENDAR_FORMATS,
} from "modules/utils/Constants";
import {
  sortUsersByCallout,
  sortUsersByEmailAddress,
  getDistributionGroupMention,
  getUserFullName,
} from "modules/utils/Functions";

interface IProps {
  id: string;
  accessUserIds?: string[];
  removedUserList?: RemovedUserFragment[];
}

const DistributionGroupTooltipContent = ({
  id,
  accessUserIds,
  removedUserList,
}: IProps) => {
  const { distributionGroupsById } = useDistributionGroups();
  const { getUsersById } = useUsers();
  const distributionGroup = distributionGroupsById[id];
  const { getUrl } = useSearchFilter();

  const noAccessUserIdList = useMemo(() => {
    if (!distributionGroup || !accessUserIds) return [];
    return [
      ...distributionGroup.userIdList,
      ...distributionGroup.emailUserIdList,
    ].filter((userId) => !accessUserIds.includes(userId));
  }, [accessUserIds, distributionGroup]);

  const noAccessUserList = useMemo(
    () => getUsersById(noAccessUserIdList).sort(sortUsersByCallout),
    [getUsersById, noAccessUserIdList],
  );

  const emailUserList = useMemo(
    () =>
      getUsersById(
        (distributionGroup?.emailUserIdList || []).filter(
          (emailUserId) => !noAccessUserIdList.includes(emailUserId),
        ),
      ).sort(sortUsersByEmailAddress),
    [distributionGroup?.emailUserIdList, getUsersById, noAccessUserIdList],
  );

  const userList = useMemo(
    () =>
      getUsersById(
        (distributionGroup?.userIdList || []).filter(
          (emailUserId) => !noAccessUserIdList.includes(emailUserId),
        ),
      ).sort(sortUsersByEmailAddress),
    [distributionGroup?.userIdList, getUsersById, noAccessUserIdList],
  );

  if (!distributionGroup) return null;

  return (
    <VStack alignItems="flex-start">
      <HStack>
        <Link
          href={getUrl(
            TAG_GROUP_THREADS_PATH,
            undefined,
            ...[
              {
                type: SearchFilterType.TypeList,
                stringListValue: [MessageThreadType.Thread],
              },
              {
                type: SearchFilterType.DistributionGroupIdList,
                objectIdListValue: [distributionGroup?.id],
              },
            ],
          )}
          w="full"
          py={0.5}
          color={colors.blue}
          fontWeight={700}
          fontSize={18}
        >
          {getDistributionGroupMention(distributionGroup)}
          {(removedUserList?.length ?? 0) > 0 && "*"}
        </Link>
        {distributionGroup.emailUserIdList.length > 0 && (
          <ExternalIcon fill={colors.transparent} />
        )}
      </HStack>
      <Text
        fontWeight={700}
        fontSize={14}
        color={colors.gray50}
      >{`Members (${userList.length})`}</Text>
      <Divider />
      <Stack spacing={1} divider={<Divider />} w="full">
        {userList.map((user) => (
          <UserItem
            key={user.id}
            user={user}
            distributionGroup={distributionGroup}
            isDisplayNotificationIcon
          />
        ))}
      </Stack>
      {!!emailUserList.length && (
        <Stack pt={4} w="full">
          <Text
            fontWeight={700}
            fontSize={14}
            color={colors.gray50}
          >{`External Contacts (${emailUserList.length})`}</Text>
          <Divider />
          <Stack spacing={1} divider={<Divider />}>
            {emailUserList.map((emailUser) => (
              <ExternalUserItem key={emailUser.id} emailUser={emailUser} />
            ))}
          </Stack>
        </Stack>
      )}
      {!!noAccessUserList.length && (
        <Stack pt={4} w="full">
          <Text
            fontWeight={700}
            fontSize={14}
            color={colors.gray50}
          >{`Members without Access (${noAccessUserList.length})`}</Text>
          <Divider />
          <Stack spacing={1} divider={<Divider />}>
            {noAccessUserList.map((user) => {
              const removedTime = removedUserList?.find(
                ({ userId }) => userId === user.id,
              )?.createTime;
              return (
                <Box key={user.id} opacity={0.6}>
                  <HStack>
                    {user.roleType === UserRoleType.Email ? (
                      <ExternalUserItem
                        emailUser={user}
                        withoutAccess
                        removedTime={removedTime}
                      />
                    ) : (
                      <UserItem
                        distributionGroup={distributionGroup}
                        user={user}
                        isDisplayNotificationIcon={false}
                        withoutAccess
                        removedTime={removedTime}
                      />
                    )}
                    <EyeSlashIcon boxSize={4} fill={colors.gray30} />
                  </HStack>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      )}
    </VStack>
  );
};

const RemovedText: React.FC<{ removedTime: Date }> = ({ removedTime }) => (
  <Text color={colors.grayMain} fontSize={12} fontWeight={400}>
    {`Removed ${moment(removedTime).calendar(COMMON_DATE_CALENDAR_FORMATS)}`}
  </Text>
);

const UserItem: React.FC<{
  distributionGroup: DistributionGroupFragment;
  user: UserFragment;
  isDisplayNotificationIcon: boolean;
  withoutAccess?: boolean;
  removedTime?: Date;
}> = ({
  distributionGroup,
  user,
  isDisplayNotificationIcon,
  withoutAccess,
  removedTime,
}) => (
  <Box w="100%" key={user.id}>
    <HStack>
      <UserAvatar userId={user.id} size="xs" displayOnlineIndicator />
      <VStack align="start" spacing={0}>
        <UserMention
          id={user.id}
          overrideColor={withoutAccess ? colors.grayMain : undefined}
        />
        {removedTime && <RemovedText removedTime={removedTime} />}
      </VStack>
      <Spacer />
      <Box>
        {isDisplayNotificationIcon &&
          !distributionGroup.notificationUserIdList.includes(user.id) && (
            <BellOutlineSlashIcon boxSize={4} fill={colors.grayMain} />
          )}
      </Box>
    </HStack>
  </Box>
);

const ExternalUserItem: React.FC<{
  emailUser: UserFragment;
  withoutAccess?: boolean;
  removedTime?: Date;
}> = ({ emailUser, withoutAccess, removedTime }) => (
  <Box w="100%" key={emailUser.id}>
    <HStack>
      <UserEmailCircleIcon boxSize={6} fill={colors.white} />
      <VStack align="start" spacing={0}>
        {emailUser.firstName && (
          <Text
            fontSize={14}
            color={withoutAccess ? colors.grayMain : colors.gray50}
          >
            {getUserFullName(emailUser)}
          </Text>
        )}
        <Text
          fontSize={14}
          color={withoutAccess ? colors.grayMain : colors.blueGreen}
        >
          {emailUser.email}
        </Text>
        {removedTime && <RemovedText removedTime={removedTime} />}
      </VStack>
      <Spacer />
    </HStack>
  </Box>
);

export default memo(DistributionGroupTooltipContent);
